import React from "react";
import {
    Box,
    Flex,
    Text,
    Stack,
    Badge,
    Icon,
    Divider,
    Tooltip,
} from "@chakra-ui/react";
import {
    MapPin,
    History,
    Phone,
    BarChart2,
    Users,
    Mail,
    UserCheck,
    Calendar,
} from "lucide-react";
import ReusableAvatar from "components/Avatar/ReusableAvatar";
import { formatDate } from "utils/dateFormatter";

const PrivacySettingRow = ({ label, enabled, icon, tooltip }) => (
    <Flex
        alignItems="center"
        justifyContent="space-between"
        p={3}
        borderWidth="1px"
        borderRadius="md"
        _hover={{ bg: "gray.50" }}
    >
        <Flex alignItems="center">
            <Tooltip label={tooltip} hasArrow>
                <Box mr={3}>{icon}</Box>
            </Tooltip>
            <Text fontWeight="medium" fontSize={"14"} color="gray.400">{label}</Text>
        </Flex>
        <Badge colorScheme={enabled ? "green" : "red"} fontWeight="semibold" borderRadius="md">
            {enabled ? "Enabled" : "Disabled"}
        </Badge>
    </Flex>
);

const ViewPrivacySettings = ({ userInfo, privacySettings }) => {
    if (!userInfo || !privacySettings) return null;

    const {
        allow_location_access,
        save_delivery_history,
        notify_on_new_device_login,
        restrict_data_analytics,
        restrict_third_party_sharing,
        object_to_marketing_emails,
        object_to_profiling,
        is_archived,
        created_at,
    } = privacySettings;

    return (
        <Box p={6} width="100%" maxW="600px" mx="auto">
            {/* Header: Avatar, Name, Status */}
            <Flex alignItems="center" mb={4}>
                <ReusableAvatar
                    name={`${userInfo.first_name} ${userInfo.last_name}`}
                    size="lg"
                />
                <Box ml={4}>
                    <Text fontSize="2xl" fontWeight="bold" noOfLines={1}>
                        {userInfo.first_name}{" "}
                        {userInfo.middle_name ? `${userInfo.middle_name} ` : ""}
                        {userInfo.last_name}
                    </Text>
                    <Badge colorScheme={userInfo.status === "Active" ? "green" : "red"} mt={1} borderRadius="md">
                        {userInfo.status}
                    </Badge>
                </Box>
            </Flex>

            <Flex alignItems="center" color="gray.500" mb={6}>
                <Icon as={Calendar} mr={2} />
                <Text fontSize="sm">Created on: {formatDate(created_at)}</Text>
            </Flex>

            <Divider mb={6} />

            {/* Privacy Settings List */}
            <Stack spacing={4}>
                <PrivacySettingRow
                    label="Allow Location Access"
                    enabled={allow_location_access}
                    icon={<MapPin size={20} color={allow_location_access ? "gray" : "gray"} />}
                    tooltip="Allow app to access your location"
                />
                <PrivacySettingRow
                    label="Save Delivery History"
                    enabled={save_delivery_history}
                    icon={<History size={20} color={save_delivery_history ? "gray" : "gray"} />}
                    tooltip="Save your delivery history for future reference"
                />
                <PrivacySettingRow
                    label="Notify on New Device Login"
                    enabled={notify_on_new_device_login}
                    icon={<Phone size={20} color={notify_on_new_device_login ? "gray" : "gray"} />}
                    tooltip="Get notifications when your account is accessed on a new device"
                />
                <PrivacySettingRow
                    label="Restrict Data Analytics"
                    enabled={restrict_data_analytics}
                    icon={<BarChart2 size={20} color={restrict_data_analytics ? "gray" : "gray"} />}
                    tooltip="Restrict your data from being used in analytics"
                />
                <PrivacySettingRow
                    label="Restrict Third Party Sharing"
                    enabled={restrict_third_party_sharing}
                    icon={<Users size={20} color={restrict_third_party_sharing ? "gray" : "gray"} />}
                    tooltip="Prevent sharing your data with third parties"
                />
                <PrivacySettingRow
                    label="Object to Marketing Emails"
                    enabled={object_to_marketing_emails}
                    icon={<Mail size={20} color={object_to_marketing_emails ? "gray" : "gray"} />}
                    tooltip="Opt out from marketing emails"
                />
                <PrivacySettingRow
                    label="Object to Profiling"
                    enabled={object_to_profiling}
                    icon={<UserCheck size={20} color={object_to_profiling ? "gray" : "gray"} />}
                    tooltip="Object to data profiling for personalized ads"
                />
            </Stack>
        </Box>
    );
};

export default ViewPrivacySettings;
